import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { GripVerticalIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslations } from "use-intl";
import { questionsQueryOptions } from "$/modules/question/lib/queries";
import { CreateOrEditQuestionModal } from "$/modules/question/ui/create-or-edit-question-modal";
import {
	persistQuestionOrder,
	sortQuestionsByStoredOrder,
} from "$/modules/questionnaire/lib/question-order";
import { QuestionCard } from "$/modules/questionnaire/ui/question-card";
import { ConstructionHeader } from "$/modules/shared/ui/construction-header";
import { NoEntriesContainer } from "$/modules/shared/ui/no-entries";

export const Route = createFileRoute(
	"/$locale/$userId/projects/$projectId/questionnaire/$questionnaireId/",
)({
	component: QuestionPage,
	loader: async ({ params, context: { queryClient } }) => {
		await queryClient.ensureQueryData({
			...questionsQueryOptions(
				params.userId,
				params.projectId,
				params.questionnaireId,
			),
			revalidateIfStale: true,
		});
	},
});

function QuestionPage() {
	const t = useTranslations("project.question");
	const { userId, projectId, questionnaireId } = Route.useParams();
	const { data: allQuestions } = useSuspenseQuery(
		questionsQueryOptions(userId, projectId, questionnaireId),
	);
	const [draggedQuestionId, setDraggedQuestionId] = useState<string | null>(
		null,
	);
	const [dragOverQuestionId, setDragOverQuestionId] = useState<string | null>(
		null,
	);
	const [orderedQuestions, setOrderedQuestions] = useState(() =>
		sortQuestionsByStoredOrder(questionnaireId, allQuestions.rows),
	);

	useEffect(() => {
		setOrderedQuestions(
			sortQuestionsByStoredOrder(questionnaireId, allQuestions.rows),
		);
	}, [allQuestions.rows, questionnaireId]);

	const reorderQuestions = (fromQuestionId: string, toQuestionId: string) => {
		if (fromQuestionId === toQuestionId) return;

		setOrderedQuestions((prevQuestions) => {
			const fromIndex = prevQuestions.findIndex(
				(question) => question.$id === fromQuestionId,
			);
			const toIndex = prevQuestions.findIndex(
				(question) => question.$id === toQuestionId,
			);

			if (fromIndex < 0 || toIndex < 0) return prevQuestions;

			const nextQuestions = [...prevQuestions];
			const [movedQuestion] = nextQuestions.splice(fromIndex, 1);
			nextQuestions.splice(toIndex, 0, movedQuestion);

			persistQuestionOrder(
				questionnaireId,
				nextQuestions.map((question) => question.$id),
			);

			return nextQuestions;
		});
	};

	return (
		<>
			<ConstructionHeader titleKey={"project.question.create"}>
				<CreateOrEditQuestionModal />
			</ConstructionHeader>
			<div className="mt-8">
				{allQuestions.total === 0 ? (
					<NoEntriesContainer text={t("noQuestions")} />
				) : (
					<>
						<p className="mb-5 text-sm text-foreground-500">
							Fragen per Drag & Drop sortieren
						</p>
						<div className="grid items-start gap-5 [grid-template-columns:repeat(auto-fit,minmax(min(100%,21rem),1fr))]">
							{orderedQuestions.map((question, index) => (
								// biome-ignore lint/a11y/noStaticElementInteractions: needed for native drag and drop sorting
								<div
									key={question.$id}
									draggable
									onDragStart={() => {
										setDraggedQuestionId(question.$id);
									}}
									onDragOver={(dragEvent) => {
										dragEvent.preventDefault();
										setDragOverQuestionId(question.$id);
									}}
									onDrop={(dragEvent) => {
										dragEvent.preventDefault();

										if (!draggedQuestionId) return;

										reorderQuestions(draggedQuestionId, question.$id);
										setDraggedQuestionId(null);
										setDragOverQuestionId(null);
									}}
									onDragEnd={() => {
										setDraggedQuestionId(null);
										setDragOverQuestionId(null);
									}}
									className={
										"min-w-0 cursor-grab rounded-2xl transition-all duration-200 active:cursor-grabbing"
									}
								>
									<div
										className={
											"mb-2 flex items-center gap-2 px-1 text-xs font-medium text-foreground-500"
										}
									>
										<GripVerticalIcon className={"size-4"} />
										<p>{`#${String(index + 1).padStart(2, "0")}`}</p>
									</div>
									<div
										className={
											dragOverQuestionId === question.$id
												? "rounded-2xl ring-2 ring-primary ring-offset-2 ring-offset-background"
												: ""
										}
									>
										<QuestionCard question={question} index={index + 1} />
									</div>
								</div>
							))}
						</div>
					</>
				)}
			</div>
		</>
	);
}
